import { useState } from "react";
import type { Ticket } from "../types.ts";
import { ticketContactEmails } from "../utils/ticketDisplay.ts";
import { EmailPredictiveInput } from "./EmailPredictiveInput.tsx";

export function ContactsPanel({ ticket, onSaved, embedded = false }: {
  ticket: Ticket;
  onSaved: (fields: { contact_emails: string[]; contact_phones: string[] }) => Promise<void>;
  embedded?: boolean;
}) {
  const initialEmails = ticketContactEmails(ticket);
  const initialPhones = ticket.contact_phones || [];
  const [emails, setEmails] = useState<string[]>(initialEmails);
  const [phones, setPhones] = useState<string[]>(initialPhones);
  const [editingEmail, setEditingEmail] = useState<{ idx: number; value: string } | null>(null);
  const [editingPhone, setEditingPhone] = useState<{ idx: number; value: string } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const dirty = JSON.stringify(emails) !== JSON.stringify(initialEmails)
    || JSON.stringify(phones) !== JSON.stringify(initialPhones);

  const addEmail = () => {
    const v = newEmail.trim().toLowerCase();
    if (!v) return;
    if (!emails.some((x) => x.toLowerCase() === v)) setEmails((prev) => [...prev, v]);
    setNewEmail("");
  };
  const addPhone = () => {
    const v = newPhone.trim();
    if (!v) return;
    if (!phones.includes(v)) setPhones((prev) => [...prev, v]);
    setNewPhone("");
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await onSaved({ contact_emails: emails, contact_phones: phones });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: "0.4rem" };
  const inputStyle: React.CSSProperties = {
    flex: 1, padding: "0.3rem 0.5rem", border: "1.5px solid var(--border,#e0e4e0)",
    borderRadius: 6, fontSize: "0.8rem",
  };
  const iconBtnStyle: React.CSSProperties = {
    border: "none", background: "transparent", cursor: "pointer", fontSize: "0.85rem", padding: "0.1rem 0.3rem",
  };
  const shellStyle: React.CSSProperties = embedded
    ? {}
    : { border: "1px solid var(--border,#e0e4e0)", borderRadius: 8, padding: "0.75rem 0.9rem", marginTop: "0.6rem" };

  return (
    <div style={shellStyle}>
      {!embedded && <div style={{ fontSize: "0.8rem", fontWeight: 700, marginBottom: "0.5rem" }}>連絡先</div>}
      {err && <div style={{ color: "var(--danger,#e53935)", fontSize: "0.75rem", marginBottom: "0.4rem" }}>{err}</div>}

      <div style={{ fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.25rem" }}>メール（全件が顧客通知の宛先）</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", marginBottom: "0.4rem" }}>
        {emails.map((e, idx) => (
          <div key={idx} style={rowStyle}>
            {editingEmail?.idx === idx ? (
              <>
                <EmailPredictiveInput
                  value={editingEmail.value}
                  onChange={(v) => setEditingEmail({ idx, value: v })}
                  autoFocus
                  exclude={emails.filter((_, i) => i !== idx)}
                  company={ticket.company}
                  serial={ticket.unit_serial}
                  mac={ticket.device_mac}
                  className="support-contact-input"
                />
                <button type="button" onClick={() => { const v = editingEmail.value.trim(); if (v) setEmails((prev) => prev.map((x, i) => (i === idx ? v : x))); setEditingEmail(null); }} title="確定" style={{ ...iconBtnStyle, color: "var(--primary,#358b39)" }}>✓</button>
                <button type="button" onClick={() => setEditingEmail(null)} title="取消" style={iconBtnStyle}>✕</button>
              </>
            ) : (
              <>
                <span className="mono support-contact-value">{e}</span>
                <button type="button" onClick={() => setEditingEmail({ idx, value: e })} title="編集" style={iconBtnStyle}>✎</button>
                <button type="button" onClick={() => setEmails((prev) => prev.filter((_, i) => i !== idx))} title="削除" style={{ ...iconBtnStyle, color: "var(--danger,#e53935)" }}>×</button>
              </>
            )}
          </div>
        ))}
        {emails.length === 0 && <div style={{ fontSize: "0.78rem", color: "var(--text-disabled,#aeb2ae)" }}>（未登録）</div>}
      </div>
      <div className="support-contact-add-row">
        <EmailPredictiveInput
          value={newEmail}
          onChange={setNewEmail}
          onEnter={addEmail}
          placeholder="メールアドレス（2文字以上で候補）"
          exclude={emails}
          company={ticket.company}
          serial={ticket.unit_serial}
          mac={ticket.device_mac}
        />
        <button type="button" onClick={addEmail} className="support-contact-add-btn" title="追加" aria-label="メールを追加">＋</button>
      </div>

      <div style={{ fontSize: "0.72rem", color: "var(--text-secondary,#5f635f)", marginBottom: "0.25rem" }}>電話</div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", marginBottom: "0.4rem" }}>
        {phones.map((p, idx) => (
          <div key={idx} style={rowStyle}>
            {editingPhone?.idx === idx ? (
              <>
                <input value={editingPhone.value} onChange={(ev) => setEditingPhone({ idx, value: ev.target.value })} autoFocus style={inputStyle} />
                <button type="button" onClick={() => { const v = editingPhone.value.trim(); if (v) setPhones((prev) => prev.map((x, i) => (i === idx ? v : x))); setEditingPhone(null); }} title="確定" style={{ ...iconBtnStyle, color: "var(--primary,#358b39)" }}>✓</button>
                <button type="button" onClick={() => setEditingPhone(null)} title="取消" style={iconBtnStyle}>✕</button>
              </>
            ) : (
              <>
                <span className="mono support-contact-value">{p}</span>
                <button type="button" onClick={() => setEditingPhone({ idx, value: p })} title="編集" style={iconBtnStyle}>✎</button>
                <button type="button" onClick={() => setPhones((prev) => prev.filter((_, i) => i !== idx))} title="削除" style={{ ...iconBtnStyle, color: "var(--danger,#e53935)" }}>×</button>
              </>
            )}
          </div>
        ))}
        {phones.length === 0 && <div style={{ fontSize: "0.78rem", color: "var(--text-disabled,#aeb2ae)" }}>（未登録）</div>}
      </div>
      <div className="support-contact-add-row">
        <input
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          placeholder="電話番号"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addPhone(); } }}
          className="support-contact-input"
        />
        <button type="button" onClick={addPhone} className="support-contact-add-btn" title="追加" aria-label="電話を追加">＋</button>
      </div>

      <button type="button" onClick={save} disabled={!dirty || saving} className="support-btn-primary" style={{ opacity: dirty ? 1 : 0.6 }}>
        {saving ? "保存中…" : "連絡先を保存"}
      </button>
    </div>
  );
}
